import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { billingAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Receipt, Save, Building2, MapPin } from 'lucide-react';

export default function BillingPage() {
  const { user } = useAuth();
  const [billing, setBilling] = useState({
    company_name: '', vat_number: '', fiscal_code: '',
    address: '', city: '', postal_code: '', province: '',
    country: 'Italia', pec: '', sdi_code: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadBilling(); }, []);

  const loadBilling = async () => {
    try {
      const res = await billingAPI.get();
      setBilling(res.data);
    } catch {
      toast.error('Errore nel caricamento dati di fatturazione');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await billingAPI.update(billing);
      toast.success('Dati di fatturazione salvati');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setBilling(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="billing-page-title">
          Dati di Fatturazione
        </h1>
        <p className="text-muted-foreground mt-1">
          Inserisci i dati necessari per la fatturazione elettronica
        </p>
      </div>

      {/* Company Info */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Building2 className="w-5 h-5 text-blue-500" />
            Dati Aziendali
          </CardTitle>
          <CardDescription>Ragione sociale e dati fiscali</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label>Ragione Sociale / Nome</Label>
              <Input
                data-testid="billing-company-name"
                placeholder="Es. Azienda SRL"
                value={billing.company_name}
                onChange={(e) => updateField('company_name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Partita IVA (P.IVA)</Label>
              <Input
                data-testid="billing-vat-number"
                placeholder="IT12345678901"
                value={billing.vat_number}
                onChange={(e) => updateField('vat_number', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Codice Fiscale</Label>
              <Input
                data-testid="billing-fiscal-code"
                placeholder="RSSMRA85M01H501Z"
                value={billing.fiscal_code}
                onChange={(e) => updateField('fiscal_code', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Address */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <MapPin className="w-5 h-5 text-green-500" />
            Indirizzo
          </CardTitle>
          <CardDescription>Sede legale per la fatturazione</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Indirizzo</Label>
            <Input
              data-testid="billing-address"
              placeholder="Via Roma 1"
              value={billing.address}
              onChange={(e) => updateField('address', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="space-y-2 col-span-2 sm:col-span-1">
              <Label>CAP</Label>
              <Input
                data-testid="billing-postal-code"
                placeholder="00100"
                value={billing.postal_code}
                onChange={(e) => updateField('postal_code', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Citta'</Label>
              <Input
                data-testid="billing-city"
                placeholder="Roma"
                value={billing.city}
                onChange={(e) => updateField('city', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Provincia</Label>
              <Input
                data-testid="billing-province"
                placeholder="RM"
                maxLength={2}
                value={billing.province}
                onChange={(e) => updateField('province', e.target.value.toUpperCase())}
              />
            </div>
            <div className="space-y-2">
              <Label>Paese</Label>
              <Input
                data-testid="billing-country"
                value={billing.country}
                onChange={(e) => updateField('country', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* E-Invoicing */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Receipt className="w-5 h-5 text-purple-500" />
            Fatturazione Elettronica
          </CardTitle>
          <CardDescription>Dati per la ricezione delle fatture elettroniche (SDI)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Codice SDI (Codice Destinatario)</Label>
              <Input
                data-testid="billing-sdi-code"
                placeholder="0000000"
                maxLength={7}
                value={billing.sdi_code}
                onChange={(e) => updateField('sdi_code', e.target.value.toUpperCase())}
              />
              <p className="text-xs text-muted-foreground">7 caratteri alfanumerici</p>
            </div>
            <div className="space-y-2">
              <Label>PEC (Posta Elettronica Certificata)</Label>
              <Input
                data-testid="billing-pec"
                type="email"
                placeholder="azienda@pec.it"
                value={billing.pec}
                onChange={(e) => updateField('pec', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">In alternativa al codice SDI</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-billing-btn"
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Salvataggio...' : 'Salva Dati di Fatturazione'}
        </Button>
      </div>
    </div>
  );
}
